import type { TourStep } from "../../components/common/GuidedTour";

export type Tutorial = {
  id: string;
  title: string;
  description: string;
  /** Geschätzte Dauer in Minuten (Anzeige in der Karte). */
  minutes: number;
  steps: TourStep[];
};

// Mitmach-Tutorials „Erste Schritte“ für neue Pflegedienste (v. a. in der Testphase).
// Schritte mit action:"click" führen nach dem Klick automatisch weiter,
// action:"input" lässt das Feld ausfüllen. Ziele: data-tour-Marker, Button-Text
// (findText, optional exact/within) oder Feldbeschriftung (findLabel).
// Texte bewusst in Du-Form wie die übrige App.

const VA_FORM = "#va-new-form";
const EMP_FORM = "#emp-new-form";
const QMH_SECTION = '[id^="qmh-section-"]';

const qmHandbuch: TourStep[] = [
  {
    title: "QM-Handbuch erstellen",
    body: "In wenigen Minuten steht dein QM-Handbuch: Du übernimmst das fertige Sonne-QM-Muster, öffnest einen Abschnitt und lernst, wie du Inhalte bearbeitest und freigibst.\n\nDu klickst dabei selbst – das Tutorial zeigt dir jeweils, wo.",
  },
  {
    selector: '[data-tour="nav-qmhandbuch"]',
    action: "click",
    // Unterpunkte fehlen bei eingeklappter Seitenleiste – dann öffnet der nächste Schritt den Bereich.
    optional: true,
    timeout: 1500,
    title: "QM-Handbuch öffnen",
    body: "Klicke links in der Navigation auf „QM Handbuch“.",
  },
  {
    tab: "qmhandbuch",
    // Text statt data-tour: in der Vorlagen-Ansicht trägt der Knopf keinen Marker.
    findText: "Vorlagen",
    exact: true,
    action: "click",
    timeout: 5000,
    title: "Zu den Vorlagen",
    body: "Unter „Vorlagen“ liegen die fertigen Muster von Sonne QM – passend zu ambulanter Pflege bzw. Intensivpflege.",
  },
  {
    selector: '[data-tour="platzhalter-info"]',
    optional: true,
    timeout: 4000,
    title: "Platzhalter: deine Daten werden eingesetzt",
    body: "Die Vorlagen enthalten Platzhalter wie {{PFLEGEDIENST_NAME}} oder {{NAME_QMB}}. Grün = wird automatisch mit deinen Daten befüllt. Gelb = fehlt noch und bleibt als {{…}} im Text stehen.\n\nAngaben zum Pflegedienst ergänzt du unter „Organisation“, Namen wie PDL oder QMB über die Qualifikation bei „Mitarbeitende“. Tipp: Am besten vor der Übernahme.",
  },
  {
    findText: "Ganzes Handbuch übernehmen",
    action: "click",
    timeout: 6000,
    title: "Ganzes Muster-Handbuch übernehmen",
    body: "Mit einem Klick übernimmst du alle Kapitel. Name, Adresse und Verantwortliche deines Pflegedienstes werden automatisch eingesetzt.\n\nWichtig: Vorhandene Inhalte im Handbuch werden dabei ersetzt.",
  },
  {
    findText: "Bestätigen",
    exact: true,
    action: "click",
    title: "Übernahme bestätigen",
    body: "Bestätige die Übernahme. Das Kopieren aller Kapitel dauert ein paar Sekunden.",
  },
  {
    // Erscheint nur, wenn Platzhalter nicht automatisch befüllt werden konnten.
    waitForText: "Vorlage übernommen",
    findText: "Bestätigen",
    exact: true,
    action: "click",
    optional: true,
    timeout: 2500,
    title: "Angaben später ergänzen",
    body: "Einige Angaben konnten nicht automatisch eingesetzt werden, weil sie im Profil deines Pflegedienstes noch fehlen. Diese Stellen sind im Text markiert – du kannst sie jederzeit ergänzen. Klicke auf „Bestätigen“.",
  },
  {
    findText: "Inhalte",
    exact: true,
    action: "click",
    title: "Zum fertigen Handbuch",
    body: "Geschafft – das Muster ist übernommen. Klicke auf „Inhalte“, um alle Kapitel zu sehen.",
  },
  {
    selector: `${QMH_SECTION} > button`,
    action: "click",
    timeout: 5000,
    title: "Einen Abschnitt öffnen",
    body: "Jedes Kapitel lässt sich aufklappen. Klicke auf den markierten Abschnitt.",
  },
  {
    findText: "Bearbeiten",
    exact: true,
    within: QMH_SECTION,
    action: "click",
    timeout: 5000,
    title: "Werkzeuge für den Abschnitt",
    body: "Über „Bearbeiten“ erreichst du alles, was du für einen Abschnitt brauchst: Inhalt bearbeiten, Vorlage übernehmen, PDF hochladen oder Mitarbeitenden zur Bearbeitung zuteilen.",
  },
  {
    findText: "In Word bearbeiten",
    within: QMH_SECTION,
    optional: true,
    title: "Inhalt in Word bearbeiten",
    body: "Hier öffnest du den Abschnitt im Word-Editor direkt im Browser – ganz ohne Installation. Änderungen werden automatisch gespeichert; zum Beenden klickst du oben auf „Schließen“.\n\nProbier es nach dem Tutorial in Ruhe aus.",
  },
  {
    findText: "Metadaten bearbeiten",
    within: QMH_SECTION,
    action: "click",
    optional: true,
    title: "Version & Freigabe",
    body: "Jeder Abschnitt hat Titel, Version, Status und Gültigkeit – das ist deine Dokumentenlenkung, wie sie der MD sehen möchte.",
  },
  {
    findLabel: "Status",
    within: QMH_SECTION,
    action: "input",
    optional: true,
    title: "Status festlegen",
    body: "„Freigegeben“ = gültig und für dein Team sichtbar, „Entwurf“ = noch in Arbeit, „Archiv“ = nicht mehr gültig.\n\nÜbernommene Muster-Abschnitte sind bereits freigegeben.",
  },
  {
    findText: "Speichern",
    exact: true,
    within: QMH_SECTION,
    action: "click",
    optional: true,
    title: "Speichern",
    body: "Speichere die Angaben. Ändere bei inhaltlichen Anpassungen auch die Version (z. B. 1.0 → 1.1).",
  },
  {
    title: "Fertig – dein QM-Handbuch steht! 🎉",
    body: "So geht es weiter:\n• Noch offene {{…}}-Platzhalter im Text ersetzen („In Word bearbeiten“) – oder Profil/Rollen ergänzen, dann werden sie bei künftigen Übernahmen automatisch befüllt.\n• Abschnitte nach und nach an deinen Pflegedienst anpassen.\n• Über „Bearbeiten“ → „Zur Bearbeitung zuteilen“ Kapitel an Kolleg:innen geben.",
  },
];

const vaErstellen: TourStep[] = [
  {
    title: "Verfahrensanweisung erstellen",
    body: "Verfahrensanweisungen (VA) regeln, wie Abläufe in deinem Pflegedienst funktionieren. Du legst jetzt eine VA aus einer fertigen Sonne-QM-Vorlage an und gibst sie frei.",
  },
  {
    selector: '[data-tour="nav-va"]',
    action: "click",
    // Unterpunkte fehlen bei eingeklappter Seitenleiste – dann öffnet der nächste Schritt den Bereich.
    optional: true,
    timeout: 1500,
    title: "Verfahrensanweisungen öffnen",
    body: "Klicke links auf „Verfahrensanweisungen“.",
  },
  {
    tab: "va",
    findText: "Neu",
    exact: true,
    action: "click",
    timeout: 5000,
    title: "Neue VA anlegen",
    body: "Klicke auf „Neu“.",
  },
  {
    findText: "Vorlage auswählen",
    within: VA_FORM,
    action: "click",
    title: "Aus einer Vorlage erstellen",
    body: "Am schnellsten geht es mit einer fertigen Vorlage. Sie wird als eigene Kopie übernommen – mit den Daten deines Pflegedienstes befüllt.\n\n(„Neue Verfahrensanweisung erstellen“ legt eine leere VA an.)",
  },
  {
    selector: '[data-tour="platzhalter-info"]',
    optional: true,
    title: "Wird automatisch befüllt",
    body: "Auch VA-Vorlagen enthalten Platzhalter wie {{PFLEGEDIENST_NAME}} oder {{NAME_PDL}}. Sonne QM setzt beim Übernehmen deine Daten ein – aus dem Profil und den Rollen deiner Mitarbeitenden. Klappe die Übersicht auf, um zu sehen, was schon befüllt wird und was noch fehlt.",
  },
  {
    findLabel: "Vorlagen durchsuchen",
    within: VA_FORM,
    action: "input",
    optional: true,
    title: "Vorlage suchen",
    body: "Tippe ein Stichwort ein, z. B. „Hygiene“, „Notfall“ oder „Aufnahme“.",
  },
  {
    findText: ["Vorlage auswählen", "Erneut übernehmen"],
    within: VA_FORM,
    action: "click",
    timeout: 5000,
    title: "Vorlage wählen",
    body: "Klicke bei der passenden Vorlage auf „Vorlage auswählen“ (bzw. „Erneut übernehmen“, wenn du sie schon einmal genutzt hast).\n\nKeine Treffer? Lösche den Suchbegriff wieder – dann siehst du alle Vorlagen.",
  },
  {
    findLabel: "Titel",
    within: VA_FORM,
    action: "input",
    requireValue: true,
    title: "Titel und Version",
    body: "Titel und Version sind aus der Vorlage übernommen. Passe den Titel bei Bedarf an.",
  },
  {
    findLabel: "Verantwortlich",
    within: VA_FORM,
    action: "input",
    title: "Verantwortlich",
    body: "Wähle aus, wer für diese VA zuständig ist – z. B. die PDL oder QMB.",
  },
  {
    findLabel: "Status",
    within: VA_FORM,
    action: "input",
    title: "Entwurf oder gleich freigeben?",
    body: "Wähle „Freigegeben“, wenn die VA sofort gelten soll. Nur freigegebene VAs werden deinem Team zur Kenntnisnahme geschickt. „Entwurf“ kannst du später freigeben.",
  },
  {
    findLabel: "Gültig bis",
    within: VA_FORM,
    action: "input",
    optional: true,
    title: "Gültigkeit",
    body: "Bis wann gilt die VA? Standard ist ein Jahr – danach erinnert dich Sonne QM an die Überprüfung.",
  },
  {
    findText: "Anlegen",
    exact: true,
    within: VA_FORM,
    action: "click",
    title: "VA anlegen",
    body: "Klicke auf „Anlegen“. Die Word-Datei der Vorlage wird dabei für deinen Pflegedienst kopiert.",
  },
  {
    waitForText: "Verfahrensanweisung gespeichert",
    findText: "Bearbeiten",
    exact: true,
    action: "click",
    timeout: 8000,
    title: "Deine neue VA",
    body: "Die VA steht jetzt oben in der Liste. Klicke auf „Bearbeiten“, um Inhalt und Status zu ändern.",
  },
  {
    findText: "In Word bearbeiten",
    optional: true,
    title: "Inhalt anpassen",
    body: "Hier öffnest du die VA im Word-Editor im Browser. Änderungen werden automatisch gespeichert; mit „Schließen“ beendest du den Editor.",
  },
  {
    findText: "Status speichern",
    optional: true,
    title: "Später freigeben",
    body: "Ist die VA noch ein Entwurf, wählst du daneben „Freigegeben“ und klickst auf „Status speichern“.",
  },
  {
    title: "Fertig – deine erste VA ist angelegt! 🎉",
    body: "Damit dein Team sie liest: Öffne unter „Mitarbeitende“ eine Person, wähle bei „Dokumente“ die Kachel „Verfahrensanweisungen“ und hake die VA an. Die Person erhält eine E-Mail und bestätigt die Kenntnisnahme digital.",
  },
];

const mitarbeitende: TourStep[] = [
  {
    title: "Mitarbeitende anlegen",
    body: "Du legst jetzt eine Kollegin oder einen Kollegen an.\n\nWichtig: Die Person bekommt beim Anlegen sofort eine E-Mail mit ihren Zugangsdaten. Verwende deshalb eine echte E-Mail-Adresse einer Kollegin/eines Kollegen – nicht deine eigene.\n\nIn der Testphase kannst du bis zu 2 Mitarbeitende anlegen.",
  },
  {
    selector: '[data-tour="nav-mitarbeitende"]',
    action: "click",
    // Unterpunkte fehlen bei eingeklappter Seitenleiste – dann öffnet der nächste Schritt den Bereich.
    optional: true,
    timeout: 1500,
    title: "Mitarbeitende öffnen",
    body: "Klicke links auf „Mitarbeitende“.",
  },
  {
    tab: "mitarbeitende",
    findText: "Neuen Mitarbeiter (Worker/Admin) anlegen",
    action: "click",
    timeout: 5000,
    title: "Neue Person anlegen",
    body: "Klicke auf „Neuen Mitarbeiter (Worker/Admin) anlegen“.",
  },
  {
    findLabel: "E-Mail",
    within: EMP_FORM,
    action: "input",
    requireValue: true,
    title: "E-Mail-Adresse",
    body: "Trage die dienstliche E-Mail-Adresse der Person ein. Damit meldet sie sich später an.",
  },
  {
    findLabel: "Vorname",
    within: EMP_FORM,
    action: "input",
    requireValue: true,
    title: "Vorname",
    body: "Trage den Vornamen ein.",
  },
  {
    findLabel: "Nachname",
    within: EMP_FORM,
    action: "input",
    requireValue: true,
    title: "Nachname",
    body: "Trage den Nachnamen ein.",
  },
  {
    findLabel: "Eintrittsdatum",
    within: EMP_FORM,
    action: "input",
    title: "Eintrittsdatum",
    body: "Ist bereits mit heute vorbelegt – passe es bei Bedarf an.",
  },
  {
    selector: '[data-tour="emp-qualifikationen"]',
    action: "input",
    title: "Qualifikation / Rolle",
    body: "Wähle eine oder mehrere Rollen, z. B. „Pflegefachkraft“, „Pflegedienstleitung“ oder „Qualitätsmanagementbeauftragte/r“. Rollen wie Pflegedienstleitung oder QMB setzen den Namen automatisch in deine Vorlagen und das QM-Handbuch ein.",
  },
  {
    findLabel: "Rechte",
    within: EMP_FORM,
    action: "input",
    title: "Rechte",
    body: "„Mitarbeiter“ = liest und bestätigt Dokumente. „Admin“ = darf alles bearbeiten, z. B. PDL oder QMB.",
  },
  {
    findLabel: "Passwort (optional)",
    within: EMP_FORM,
    optional: true,
    title: "Passwort leer lassen",
    body: "Lass das Feld am besten leer: Sonne QM erzeugt dann ein sicheres Passwort und schickt es der Person per E-Mail.",
  },
  {
    findText: "Verfahrensanweisungen",
    within: EMP_FORM,
    optional: true,
    title: "VAs zur Kenntnisnahme zuweisen",
    body: "Über diese Kachel weist du der Person Verfahrensanweisungen zu. Sie bestätigt das Lesen dann digital – ein wichtiger Nachweis für die MD-Prüfung.",
  },
  {
    findText: "Anlegen",
    exact: true,
    within: EMP_FORM,
    action: "click",
    title: "Person anlegen",
    body: "Klicke auf „Anlegen“. Die Person erhält jetzt ihre Willkommens-E-Mail.",
  },
  {
    waitForText: "Mitarbeitende angelegt",
    timeout: 8000,
    title: "Fertig – dein Team wächst! 🎉",
    body: "Die Person steht jetzt in der Liste. Über einen Klick auf den Namen pflegst du später Qualifikationen, Nachweise und zugewiesene VAs.",
  },
];

const TR_FORM = "#tr-new-form";
const TR_ITEM = '[id^="tr-item-"]';
const TR_TEILNAHME = '[data-tour="tr-teilnahme"]';

const fortbildungen: TourStep[] = [
  {
    title: "Fortbildung planen & Zertifikat erstellen",
    body: "Du planst eine Fortbildung, legst fest, wer teilnehmen muss, dokumentierst die Durchführung und erzeugst die Teilnahme-Zertifikate.\n\nFortbildungsnachweise gehören zu den Dingen, die der MD regelmäßig prüft.",
  },
  {
    selector: '[data-tour="nav-fortbildungen"]',
    action: "click",
    // Unterpunkte fehlen bei eingeklappter Seitenleiste – dann öffnet der nächste Schritt den Bereich.
    optional: true,
    timeout: 1500,
    title: "Fortbildungen öffnen",
    body: "Klicke links auf „Fortbildung“.",
  },
  {
    tab: "fortbildungen",
    selector: '[data-tour="fortbildung-neu"]',
    action: "click",
    timeout: 5000,
    title: "Neue Fortbildung",
    body: "Klicke auf „Neu“.",
  },
  {
    findText: "Aus Fortbildungsbibliothek auswählen",
    action: "click",
    title: "Aus der Bibliothek übernehmen",
    body: "Am schnellsten geht es mit einer fertigen Fortbildung aus der Bibliothek: Thema, Inhalte fürs Zertifikat und alle Dokumente des Eintrags werden übernommen.\n\n(„Neu anlegen“ startet mit einem leeren Formular.)",
  },
  {
    selector: '[data-tour="library-years"]',
    optional: true,
    timeout: 3000,
    title: "Nach Jahren sortiert",
    body: "Die Bibliothek ist nach Jahren aufgeteilt – vorausgewählt ist das aktuelle Jahr. Ältere Jahrgänge oder „Alle Jahre“ erreichst du über die Reiter.",
  },
  {
    findLabel: "Suche...",
    action: "input",
    optional: true,
    title: "Fortbildung suchen",
    body: "Tippe ein Stichwort ein, z. B. „Erste Hilfe“, „Datenschutz“ oder „SIS“. Mit der Auswahl daneben filterst du nach Kategorie.",
  },
  {
    findText: "Auswählen",
    exact: true,
    action: "click",
    timeout: 5000,
    title: "Fortbildung wählen",
    body: "Klicke bei der passenden Fortbildung auf „Auswählen“.\n\nKeine Treffer? Lösche den Suchbegriff wieder.",
  },
  {
    findLabel: "Thema",
    within: TR_FORM,
    action: "input",
    requireValue: true,
    timeout: 5000,
    title: "Thema",
    body: "Ist aus der Bibliothek übernommen. Passe es bei Bedarf an.",
  },
  {
    findLabel: "Datum",
    within: TR_FORM,
    action: "input",
    requireValue: true,
    title: "Wann findet sie statt?",
    body: "Trage den geplanten Termin ein (vorbelegt mit heute).",
  },
  {
    findLabel: "Dozent/in",
    within: TR_FORM,
    action: "input",
    optional: true,
    title: "Dozent/in",
    body: "Wer leitet die Fortbildung? Der Name erscheint später auf dem Zertifikat.",
  },
  {
    selector: `${TR_FORM} [data-tour="training-documents"]`,
    optional: true,
    title: "Dokumente",
    body: "Hier hängst du bis zu 5 Dokumente an – PDF, Word, PowerPoint, JPG oder PNG, z. B. Präsentation, Handout oder Teilnehmerliste. Dokumente aus der Bibliothek sind schon dabei.",
  },
  {
    selector: '[data-tour="tr-pflicht"]',
    action: "input",
    optional: true,
    title: "Wer muss teilnehmen?",
    body: "Hake die Mitarbeitenden an, für die die Fortbildung Pflicht ist. Wichtig: Nur diese Personen kannst du später als „teilgenommen“ markieren und ihnen ein Zertifikat ausstellen.",
  },
  {
    findText: "Anlegen",
    exact: true,
    within: TR_FORM,
    action: "click",
    title: "Fortbildung anlegen",
    body: "Klicke auf „Anlegen“.",
  },
  {
    waitForText: "Fortbildung gespeichert",
    selector: `${TR_ITEM} > button`,
    action: "click",
    timeout: 8000,
    title: "Deine Fortbildung",
    body: "Sie steht jetzt oben in der Liste. Klappe sie auf.",
  },
  {
    findLabel: "Status",
    within: TR_ITEM,
    action: "input",
    title: "Nach der Fortbildung: „Durchgeführt“",
    body: "Ist die Fortbildung gelaufen, wählst du „Durchgeführt“. Dann fragt Sonne QM nach Datum und Ort der Durchführung – für den Nachweis.\n\nFür dieses Tutorial kannst du das jetzt schon ausprobieren.",
  },
  {
    findLabel: "Durchgeführt am",
    within: TR_ITEM,
    action: "input",
    optional: true,
    title: "Durchgeführt am",
    body: "An welchem Tag fand die Fortbildung statt?",
  },
  {
    findLabel: "Ort",
    within: TR_ITEM,
    action: "input",
    optional: true,
    title: "Ort",
    body: "Wo fand sie statt, z. B. „Büro“ oder „online“?",
  },
  {
    findText: "Speichern",
    exact: true,
    within: TR_ITEM,
    action: "click",
    title: "Speichern",
    body: "Speichere den Status.",
  },
  {
    findText: "Bearbeiten",
    exact: true,
    within: TR_TEILNAHME,
    action: "click",
    optional: true,
    timeout: 6000,
    title: "Teilnahme dokumentieren",
    body: "Bei durchgeführten Fortbildungen erscheint der Bereich „Teilnahme“. Klicke dort auf „Bearbeiten“, um die Teilnehmenden zu markieren.",
  },
  {
    selector: TR_TEILNAHME,
    action: "input",
    optional: true,
    title: "Wer war dabei?",
    body: "Hake alle an, die teilgenommen haben. Danach klickst du auf „Fertig“ und auf „Weiter“.",
  },
  {
    findText: "Zertifikate (PDF)",
    within: TR_ITEM,
    optional: true,
    title: "Zertifikate erstellen",
    body: "Ein Klick erzeugt für alle Teilnehmenden ein Zertifikat als PDF – mit Thema, Inhalten, Datum und Dozent/in. Einzelne Zertifikate gibt es direkt neben dem Namen.\n\nTipp: Oben unter „Zertifikat-Hintergrund“ kannst du ein eigenes Briefpapier (PNG) hinterlegen.",
  },
  {
    title: "Fertig – Fortbildung dokumentiert! 🎉",
    body: "Deine Mitarbeitenden sehen die Fortbildung in ihrem Bereich und bestätigen die Kenntnisnahme digital. Offene Bestätigungen zeigt dir das Dashboard – ein wichtiger Nachweis für die MD-Prüfung.",
  },
];

const verbesserungsliste: TourStep[] = [
  {
    title: "Verbesserungsliste bedienen",
    body: "In der Verbesserungsliste erfasst du Beschwerden, Fehler und Verbesserungsideen – und dokumentierst, was du dagegen tust. Genau das prüft der MD beim Beschwerde- und Fehlermanagement.",
  },
  {
    selector: '[data-tour="nav-beschwerdemanagement"]',
    action: "click",
    // Unterpunkte fehlen bei eingeklappter Seitenleiste – dann öffnet der nächste Schritt den Bereich.
    optional: true,
    timeout: 1500,
    title: "Verbesserungsliste öffnen",
    body: "Klicke links auf „Verbesserungsliste“.",
  },
  {
    tab: "beschwerdemanagement",
    findText: "Neue Beschwerde",
    exact: true,
    action: "click",
    timeout: 5000,
    title: "Neuen Eintrag erfassen",
    body: "Klicke auf „Neue Beschwerde“.",
  },
  {
    findLabel: "Titel",
    action: "input",
    requireValue: true,
    title: "Worum geht es?",
    body: "Kurzer Titel, z. B. „Einsatz verspätet“ oder „Medikament falsch gestellt“.",
  },
  {
    findLabel: "Kategorie",
    action: "input",
    requireValue: true,
    title: "Kategorie",
    body: "Ordne den Eintrag ein, z. B. „Pflege“, „Organisation“, „Hygiene“ oder „Kommunikation“.",
  },
  {
    findLabel: "Schweregrad",
    action: "input",
    requireValue: true,
    title: "Schweregrad",
    body: "Wie schwer wiegt der Vorfall? Wähle eine der vier Stufen: Gering, Mittel, Hoch oder Kritisch. Die Farbe markiert den Eintrag später in der Liste.",
  },
  {
    findLabel: "Beschreibung",
    action: "input",
    title: "Beschreibung",
    body: "Was ist passiert? Halte die Fakten kurz fest.",
  },
  {
    findLabel: "Verantwortlich",
    action: "input",
    optional: true,
    title: "Verantwortlich",
    body: "Wer kümmert sich darum?",
  },
  {
    findText: "Beschwerde speichern",
    action: "click",
    title: "Speichern",
    body: "Klicke auf „Beschwerde speichern“. Der Eintrag öffnet sich danach direkt mit allen Details.",
  },
  {
    selector: '[data-tour="beschwerde-detail"]',
    timeout: 8000,
    title: "Die Details",
    body: "Hier dokumentierst du alles weitere: wer gemeldet hat, die Ursachenanalyse und Fristen.",
  },
  {
    findLabel: "Maßnahmename",
    within: '[data-tour="massnahme-neu"]',
    action: "input",
    title: "Maßnahme planen",
    body: "Was tust du, damit es nicht wieder passiert? Gib der Maßnahme einen Namen, z. B. „Tourenplanung anpassen“.",
  },
  {
    findLabel: "Beschreibung",
    within: '[data-tour="massnahme-neu"]',
    action: "input",
    requireValue: true,
    title: "Maßnahme beschreiben",
    body: "Beschreibe die Maßnahme kurz. Darunter wählst du Art (Sofort-, Korrektur- oder Vorbeugemaßnahme), Verantwortliche, Frist und Status.",
  },
  {
    findText: "Maßnahme speichern",
    within: '[data-tour="massnahme-neu"]',
    action: "click",
    title: "Maßnahme speichern",
    body: "Speichere die Maßnahme. Überschrittene Fristen werden später rot markiert.",
  },
  {
    findLabel: "Status",
    within: '[data-tour="beschwerde-status"]',
    action: "input",
    title: "Status pflegen",
    body: "Der Status zeigt den Bearbeitungsstand: „Neu“ → „In Bearbeitung“ → „Wirksamkeit prüfen“ → „Geschlossen“. Ist alles erledigt, wählst du „Geschlossen“.",
  },
  {
    findText: "Speichern",
    exact: true,
    within: '[data-tour="beschwerde-detail"]',
    action: "click",
    title: "Änderungen speichern",
    body: "Klicke auf „Speichern“, damit der neue Status übernommen wird.",
  },
  {
    title: "Fertig – so einfach geht Verbesserung! 🎉",
    body: "Oben in der Liste filterst du nach Status, Meldenden und Verantwortlichen. So siehst du jederzeit, was offen ist – und kannst es in der MD-Prüfung belegen.",
  },
];

// Kurze Orientierung nach dem Willkommens-Dialog: wo Anleitungen und Platzhalter-Daten liegen.
export const ORIENTIERUNG: TourStep[] = [
  {
    selector: '[data-tour="erste-schritte"]',
    tab: "dashboard",
    optional: true,
    title: "Erste Schritte",
    body: "Hier im Dashboard findest du die Mitmach-Tutorials. Ein Häkchen zeigt, was du schon erledigt hast.",
  },
  {
    selector: '[aria-label="Hilfe öffnen"]',
    title: "Hilfe – jederzeit",
    body: "Über das Fragezeichen erreichst du alle Anleitungen und die Mitmach-Tutorials – auch später noch.",
  },
  {
    selector: '[aria-label="Einführungstour starten"]',
    title: "Kompass",
    body: "Der Kompass erklärt dir die Seite, auf der du gerade bist.",
  },
  {
    selector: '[data-tour="nav-organisation"]',
    optional: true,
    title: "Profil – Daten für Platzhalter",
    body: "Unter „Organisation“ pflegst du Name, Adresse, IK-Nummer und Kontaktdaten deines Pflegedienstes. Diese Angaben setzt Sonne QM automatisch in Vorlagen ein, z. B. {{PFLEGEDIENST_NAME}} oder {{STRASSE}}.",
  },
  {
    selector: '[data-tour="nav-mitarbeitende"]',
    optional: true,
    title: "Rollen – Namen für Platzhalter",
    body: "Weise deinen Mitarbeitenden Qualifikationen zu (z. B. PDL, QMB, Hygienebeauftragte/r). Ihre Namen erscheinen dann automatisch in QM-Handbuch und Verfahrensanweisungen, z. B. bei {{NAME_PDL}} oder {{NAME_QMB}}.",
  },
  {
    title: "Bereit! ☀️",
    body: "Tipp: Pflege zuerst Profil und Rollen – dann sind übernommene Vorlagen direkt mit deinen Daten befüllt. Danach geht es am besten mit „Erste Schritte“ im Dashboard weiter.",
  },
];

export const TUTORIALS: Tutorial[] = [
  { id: "qm-handbuch", title: "QM-Handbuch erstellen", description: "Muster-Handbuch übernehmen, Abschnitt öffnen und freigeben.", minutes: 4, steps: qmHandbuch },
  { id: "va-erstellen", title: "Verfahrensanweisung erstellen", description: "VA aus einer Vorlage anlegen und freigeben.", minutes: 4, steps: vaErstellen },
  { id: "mitarbeitende", title: "Mitarbeitende anlegen", description: "Person mit Rolle und Qualifikation anlegen.", minutes: 3, steps: mitarbeitende },
  { id: "fortbildungen", title: "Fortbildung & Zertifikat", description: "Fortbildung planen, Teilnahme dokumentieren, Zertifikate erstellen.", minutes: 5, steps: fortbildungen },
  { id: "verbesserungsliste", title: "Verbesserungsliste bedienen", description: "Eintrag erfassen, Maßnahme planen, abschließen.", minutes: 3, steps: verbesserungsliste },
];
